import { Box, Button, useMediaQuery } from "@mui/material";
import React, { useRef } from "react";
import Select, { components } from "react-select";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "./lead-source-attribution.scss";
import ReactApexChart from "react-apexcharts";
import ApexCharts, { ApexOptions } from "apexcharts";
import { exportMetricCampaigns } from "../../../../../api/campaign";
import { Height } from "@mui/icons-material";
import { leadSourcesNameColors } from "../../../../leads/lead-reports/lead-source-breakdown/lead-source-breakdown.constant";
interface LeadSourceAttributionProps {
  metricsData: any;
  isLoading?: boolean;
  exportChart?: (params: any, data?: any) => void; // Optional exportChart function
  enableExport?: boolean;
}
const LeadSourceAttribution: React.FC<LeadSourceAttributionProps> = ({
  metricsData,
  isLoading,
  exportChart,
  enableExport = false,
}) => {
  const chartContainerRef = useRef<any>(null);
  const [format, setFormat] = React.useState({ value: "PDF", label: "PDF" });

  const leadsSourceAttribution =
    metricsData?.leadsSourceAttribution?.sort(
      (attributionOne: any, attributionTwo: any) =>
        attributionTwo?.noOfLeads - attributionOne?.noOfLeads
    ) || [];
  const isMobile = useMediaQuery("(max-width:768px)");
  const series: any = [];
  const labels: any = [];
  const count: any = [];

  leadsSourceAttribution.forEach((item: any) => {
    series.push(Number(item.percentage.replace("%", "")));
    labels.push(item.leadSource);
    count.push(item.noOfLeads);
  });
  // const series = [44, 55, 41, 17, 15];

  const options: ApexOptions = {
    chart: {
      type: "donut" as const,
      toolbar: {
        show: false,
      },
    },
    noData: {
      text: "No data available",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#7B8A8B",
        fontSize: "18px",
        fontFamily: "Roboto, sans-serif",
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
        },
        // startAngle: 20,
      },
    },

    responsive: [
      {
        breakpoint: 1024, // 👈 mobile or tablet breakpoint
        options: {
          legend: {
            show: true,
            position: "bottom", // ✅ this moves it to bottom
            horizontalAlign: "left",
            fontSize: "12px",
          },
          chart: {
            width: "100%",
          },
        },
      },
    ],
    labels: labels || [],
    dataLabels: {
      enabled: false,
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },
    legend: {
      horizontalAlign: "left",
      fontSize: "14px",
      position: "right",
      formatter: function (val, opts) {
        return val + " - " + opts.w.globals.series[opts.seriesIndex] + "%";
      },
      floating: false,

      itemMargin: {
        horizontal: 10,
        vertical: 6,
      },
      markers: {
        size: 5,
        strokeWidth: 0,
        offsetX: -5,
      },
      width: 220,
    },

    tooltip: {
      enabled: true,
      y: {
        formatter: (value, labels) => {
          return `${value}% (${count[labels?.dataPointIndex]})`;
        },
      },
    },
    colors: leadSourcesNameColors,
  };

  const handleExport = () => {
    if (!exportChart) return;

    const params = {
      metrics: "leadsSourceAttribution",
      format: format?.value?.toLowerCase(),
    };
    exportChart(params, format.value === "PDF" ? params?.metrics : undefined);
  };

  const itemsPerRow = 4;
  const rowHeight = 30;
  const labelCount = labels.length;

  const legendRows = Math.ceil(labelCount / itemsPerRow);

  const totalChartHeight = Math.max(450, 400 + legendRows * rowHeight);

  const fullOptions: ApexOptions = {
    ...options,
    labels: labels || [],
    chart: {
      type: "donut" as const,
      id: "leadsSourceAttribution",
      height: totalChartHeight, // Adjusted height to accommodate legend
      toolbar: {
        show: false,
        tools: {
          download: true,
        },
      },
    },
    legend: {
      show: true,
      position: "bottom", // 👇 bottom placement
      horizontalAlign: "left", // 👈 horizontal layout
      floating: false,
      fontSize: "12px",
      formatter: function (val, opts) {
        return val + " - " + opts.w.globals.series[opts.seriesIndex] + "%";
      },
      itemMargin: {
        horizontal: 5,
        vertical: 5,
      },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (value) => `${value}% ()`,
      },
    },
  };

  return (
    <div className="lead-source-wrapper">
      <Box
        sx={{
          padding: "10px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <p className="header-text">Lead Source Attribution</p>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Select
            options={[
              { value: "PDF", label: "PDF" },
              { value: "CSV", label: "CSV" },
            ]}
            isSearchable={false}
            value={format} // default selected value
            onChange={(selectedOption: any) => setFormat(selectedOption)}
            className="select-dropdown"
            components={{
              DropdownIndicator: (props) => {
                return (
                  <components.DropdownIndicator {...props}>
                    <ArrowDropDownIcon
                      style={{ fontSize: 20, color: "black" }}
                    />
                  </components.DropdownIndicator>
                );
              },
              IndicatorSeparator: () => null, // ⛔️ removes vertical line
              // DropdownIndicator, // ✅ custom icon
            }}
          />
          <Button
            variant="outlined"
            onClick={handleExport}
            className="address-edit-button"
            disabled={!enableExport}
          >
            Export
          </Button>
        </Box>
      </Box>
      {!isLoading && series?.length ? (
        <div
          style={{
            position: "absolute", // Take it out of the normal document flow
            left: "-9999px", // Move it far off to the left (off-screen)
            top: "-9999px", // Move it far off to the top
            width: "800px", // Give it explicit dimensions
            height: "100%", // Give it explicit dimensions
            overflow: "hidden", // Prevent any accidental scrollbars
            boxSizing: "border-box", // Ensure padding and border are included in the width/height
          }}
          className="hidden-chart"
        >
          <ReactApexChart
            options={fullOptions}
            series={series || []}
            type="donut"
            height={totalChartHeight}
            width={1240}
          />
        </div>
      ) : (
        <></>
      )}
      <Box sx={{ padding: "0 20px" }}>
        {!isLoading && series?.length ? (
          <div
            ref={chartContainerRef}
            style={{
              width: "100%",
              minHeight: isMobile ? 400 : "auto",
              overflow: "visible",
            }}
          >
            <ReactApexChart
              options={options}
              series={series || []}
              type="donut"
              height={400}
            />
          </div>
        ) : (
          <>
            <Box className="no-data-found">
              <p
                style={{
                  textAlign: "center",
                  fontSize: "18px",
                  color: "#7B8A8B",
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 400,
                  margin: "40px 0",
                  width: "100%",
                  height: "100%",
                }}
              >
                {series?.length ? "Loading..." : " No data available"}
              </p>
            </Box>
          </>
        )}
      </Box>
    </div>
  );
};

export default LeadSourceAttribution;
